/**
 * Είσπραξη τραπεζιού από το κινητό του σερβιτόρου.
 *
 * Τρεις τρόποι, με τη σειρά που τους χρειάζεται κανείς στο πάτωμα:
 *   1. Πλήρης εξόφληση — ένα πάτημα, μετρητά ή κάρτα
 *   2. Ρέστα — δίνει ο πελάτης χαρτονόμισμα, βλέπεις τι να επιστρέψεις
 *   3. Χωριστά — διαλέγεις είδη και εισπράττεις μόνο αυτά
 *
 * ΟΛΟΙ οι υπολογισμοί έρχονται από το utils/dineInMoney, το ίδιο που
 * χρησιμοποιεί το ταμείο. Δεν αντιγράφουμε λογική χρημάτων: κάθε φορά που
 * υπήρξαν δύο αντίγραφα, απέκλιναν (προ/μετά έκπτωσης, IRIS ως κάρτα).
 *
 * Κάρτα / IRIS (5/10/2026): στέλνουμε το τερματικό όπως το ταμείο — ίδιο κλειδί
 * επιλογής στο localStorage, αλλιώς το προεπιλεγμένο, αλλιώς το πρώτο. Χωρίς αυτό
 * ο server απαντούσε 400 «Terminal ID required» σε κάθε χρέωση από το κινητό.
 * Το αίτημα ΜΕΝΕΙ ανοιχτό όσο ο πελάτης βάζει την κάρτα (έως ~3′): σε αυτό το
 * διάστημα η οθόνη δεν κλείνει, και αν χαθεί η απάντηση (Wi-Fi, κινητό σε ύπνο)
 * η χρέωση ΜΠΟΡΕΙ να έχει γίνει — τότε κλειδώνουμε την είσπραξη μέχρι «Έλεγχο».
 *
 * Κουμπιά (6/10/2026): Μετρητά και Κάρτα γρήγορα· το IRIS στα «Περισσότερα». Το «Επί πιστώσει»
 * είναι ΜΟΝΟ στο ταμείο — όχι στο PDA.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Banknote, CreditCard, Loader2, Check, Split, Smartphone, Receipt, AlertTriangle, ChevronDown, ChevronUp,
} from 'lucide-react';
import { useToast } from '../../shared/Toast';
import { dineInApi, DineInApiError } from '../../../services/dineInApi';
import {
  tablePaymentState,
  aggregatePaymentItems,
  sliceAmount,
  MONEY_TOLERANCE,
  PayableUnit,
} from '../../../utils/dineInMoney';
import { showSizeName } from '../../../utils/sellableSizes';
import type { Product } from '../../../types/order';
import { FullScreen, Button, money } from './ui';
import { cardDoubtsOf } from '../../../utils/dineInCardDoubt';
import CardDoubtBanner from './CardDoubtBanner';

interface PaymentScreenProps {
  storeId: number;
  /** Το αντικείμενο details του τραπεζιού (session, orders, paidItems) */
  details: any;
  tableNumber: string;
  /** Ο κατάλογος του app — για την απόκρυψη ονόματος μοναδικού μεγέθους (απόφαση 15) */
  products?: Product[];
  onClose: () => void;
  /** Κλήθηκε μετά από επιτυχή είσπραξη — ανανέωσε τη λίστα */
  onPaid: () => void;
}

type Mode = 'full' | 'change' | 'split';

/** Τερματικό POS όπως το δίνει το GET /pos/terminals — μόνο ό,τι χρειάζεται η οθόνη. */
interface PosTerminal {
  id: number;
  terminal_name?: string;
  is_default?: boolean;
  is_active?: boolean;
}

const terminalLabel = (t: PosTerminal) => t.terminal_name || `Τερματικό ${t.id}`;

/** ΙΔΙΟ κλειδί με το ταμείο (DineInOperations): ο υπάλληλος βρίσκει παντού το τερματικό του. */
const terminalKey = (employeeId: number, storeId: number) => `pos_terminal_${employeeId}_${storeId}`;

const readSavedTerminal = (employeeId: number | null, storeId: number): number | null => {
  if (employeeId == null) return null;
  try {
    const n = parseInt(localStorage.getItem(terminalKey(employeeId, storeId)) || '', 10);
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
};

const saveTerminal = (employeeId: number | null, storeId: number, terminalId: number) => {
  if (employeeId == null) return;
  try {
    localStorage.setItem(terminalKey(employeeId, storeId), String(terminalId));
  } catch {
    /* χωρίς αποθήκευση (ιδιωτική περιήγηση): απλώς δεν θυμόμαστε την επιλογή */
  }
};

/**
 * Ο υπάλληλος, για το κλειδί του τερματικού: το employee_id του token σύνδεσης (το ίδιο
 * με το employee.id του useAuth). ΟΧΙ useAuth εδώ: το hooks/useAuth φέρνει το services/api
 * (axios), που δεν φορτώνει στα tests του TableSheet, και ο TableSheet δεν έχει τον υπάλληλο
 * για να τον περάσει. Χωρίς token / χωρίς employee_id → null: δεν θυμόμαστε επιλογή.
 */
const tokenEmployeeId = (): number | null => {
  try {
    const part = (localStorage.getItem('xentrix_token') || '').split('.')[1];
    if (!part) return null;
    const payload = JSON.parse(atob(part.replace(/-/g, '+').replace(/_/g, '/')));
    const id = Number(payload?.employee_id);
    return Number.isInteger(id) && id > 0 ? id : null;
  } catch {
    return null;
  }
};

/** Χρέωση κάρτας/IRIS που περιμένει απάντηση από το τερματικό. */
interface CardCharge {
  amount: number;
  iris: boolean;
  terminalName: string | null;
}

/** Χρέωση χωρίς απάντηση: ίσως έγινε. Το υπόλοιπο ΠΡΙΝ τη χρέωση είναι η βάση του «Ελέγχου». */
interface Unconfirmed extends CardCharge {
  sessionId: number;
  dueBefore: number;
  /** Πατήθηκε «Έλεγχος» και περιμένουμε φρέσκα στοιχεία από τον γονέα */
  checking: boolean;
}

/** Η απόφαση «καταγράφηκε» θέλει το υπόλοιπο να πέσει όσο η χρέωση (με περιθώριο στρογγύλευσης
 *  ανά τεμάχιο)· μια άσχετη μικρή μείωση δεν πρέπει να πει «μη χρεώσεις ξανά». */
const RECORDED_SLACK = 0.05;
/** Αν δεν έρθουν φρέσκα στοιχεία (το reload του γονέα απέτυχε), το «Έλεγχος» ξαναπατιέται. */
const CHECK_RETRY_MS = 10000;
/** Όριο αναμονής της απάντησης (ίδιο με το όριο του nginx για τις κάρτες). Ο server περιμένει το
 *  τερματικό έως ~190″ και μαζί με υπογραφή/πάροχο μπορεί να απαντήσει και λίγο ΜΕΤΑ το όριο — τότε
 *  η χρέωση θεωρείται αβέβαιη και η καθυστερημένη απάντηση ξαναφορτώνει το τραπέζι. Χωρίς όριο, σε
 *  «μισάνοιχτη» σύνδεση το αίτημα δεν τελειώνει ποτέ και ο σερβιτόρος θα έμενε κλειδωμένος. */
const CARD_WAIT_MS = 200000;

/**
 * Αβέβαιες χρεώσεις ανά συνεδρία τραπεζιού. Ζουν ΕΞΩ από την οθόνη: αν ο σερβιτόρος την κλείσει
 * με το X και την ξανανοίξει, το κλείδωμα «μη χρεώσεις ξανά πριν ελέγξεις» είναι ακόμη εκεί.
 */
const pendingUnconfirmed = new Map<number, Unconfirmed>();
/** Μόνο για τα tests: καθαρή αρχή ανάμεσα στις δοκιμές. */
export const resetPendingUnconfirmed = () => pendingUnconfirmed.clear();

/**
 * 409 του φρουρού διπλής πληρωμής του server: το τραπέζι δεν είναι όπως το βλέπει το κινητό
 * (τα είδη πληρώθηκαν ήδη, ή η προηγούμενη χρέωση είναι ακόμη στο τερματικό). Δείχνουμε το
 * μήνυμά του και ξαναφορτώνουμε το τραπέζι, για να φανεί τι μένει πραγματικά απλήρωτο.
 */
const GUARD_CODES = new Set([
  'items_already_paid', 'payment_in_progress', 'amount_exceeds_balance', 'item_not_in_session',
  // όχι του φρουρού, αλλά ίδια ανάγκη: το τραπέζι έκλεισε από αλλού — δείξε το και ξαναφόρτωσε
  'session_closed',
]);

/** Η απάντηση της χρέωσης, με όριο αναμονής. Αν έρθει ΜΕΤΑ το όριο, το onLate ξαναφορτώνει το τραπέζι. */
const withCardTimeout = <T,>(request: Promise<T>, onLate: () => void): Promise<T> =>
  new Promise<T>((resolve, reject) => {
    let timedOut = false;
    const timer = setTimeout(() => {
      timedOut = true;
      reject(new Error('card_wait_timeout'));
    }, CARD_WAIT_MS);
    const late = () => { try { onLate(); } catch { /* η ανανέωση είναι βοήθεια, όχι προϋπόθεση */ } };
    request.then(
      (value) => { clearTimeout(timer); if (timedOut) late(); else resolve(value); },
      (error) => { clearTimeout(timer); if (timedOut) late(); else reject(error); }
    );
  });

const DECLINED_FALLBACK = 'Η πληρωμή δεν εγκρίθηκε από το τερματικό.';
const TERMINAL_FALLBACK = 'Δεν βρέθηκε ενεργό τερματικό κάρτας για τη χρέωση.';
const SIGNATURE_FALLBACK = 'Ο πάροχος δεν έδωσε υπογραφή για την πληρωμή — η κάρτα δεν χρεώθηκε. Δοκίμασε ξανά σε λίγο.';
const SLOT_FALLBACK = 'Η πληρωμή δεν ξεκίνησε — δοκίμασε ξανά.';

type CardFailure = { kind: 'unconfirmed' } | { kind: 'declined' | 'terminal'; message: string } | null;

/**
 * Τι σημαίνει μια αποτυχία κάρτας/IRIS. ΑΒΕΒΑΙΗ: δεν ήρθε απάντηση (δίκτυο), 5xx, ή ο server
 * δηλώνει ρητά ότι δεν ξέρει — η χρέωση μπορεί να έχει γίνει. Οριστική άρνηση: code ή, από
 * παλιό server χωρίς κωδικούς, 400 «Payment declined». null = κάθε άλλο σφάλμα (όπως πριν).
 */
const cardFailure = (error: unknown): CardFailure => {
  if (!(error instanceof DineInApiError)) return { kind: 'unconfirmed' };
  const body = error.body || {};
  const code = error.code ?? body.code;
  // Ο πάροχος δεν έδωσε υπογραφή: ο server σταμάτησε ΠΡΙΝ μιλήσει στο τερματικό — οριστικό.
  // (Παλιός server χωρίς code: πέφτει στο 5xx = αβέβαιο, την ασφαλή πλευρά.)
  if (code === 'signature_failed' && body.unconfirmed !== true) {
    return { kind: 'declined', message: body.message || SIGNATURE_FALLBACK };
  }
  // Ο φρουρός διπλής πληρωμής δεν πήρε το κλείδωμα του τραπεζιού (σφάλμα βάσης): η πληρωμή
  // δεν ξεκίνησε καθόλου, άρα τίποτα δεν έφτασε στο τερματικό — οριστικό, όχι αβέβαιο.
  if (code === 'payment_slot_failed' || body.error === 'payment_slot_failed') {
    return { kind: 'declined', message: body.message || SLOT_FALLBACK };
  }
  if (error.status >= 500 || code === 'payment_unconfirmed' || body.unconfirmed === true) {
    return { kind: 'unconfirmed' };
  }
  if (code === 'payment_declined' || (error.status === 400 && body.error === 'Payment declined')) {
    return { kind: 'declined', message: body.message || DECLINED_FALLBACK };
  }
  if (
    code === 'terminal_required' ||
    code === 'terminal_not_found' ||
    body.error === 'Terminal ID required for card payment' ||
    body.error === 'POS terminal not found or inactive'
  ) {
    return { kind: 'terminal', message: body.message || TERMINAL_FALLBACK };
  }
  return null;
};

const PaymentScreen: React.FC<PaymentScreenProps> = ({
  storeId,
  details,
  tableNumber,
  products = [],
  onClose,
  onPaid,
}) => {
  const { showToast } = useToast();
  const [mode, setMode] = useState<Mode>('full');
  const [busy, setBusy] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [given, setGiven] = useState('');
  // 409 receipt_required: ο server αρνείται είσπραξη χωρίς παραστατικό («πρώτα
  // απόδειξη, μετά είσπραξη»). Το μήνυμα μένει ορατό στην οθόνη — ένα toast
  // χάνεται σε 3″ και ο σερβιτόρος ξαναπατάει «Μετρητά» χωρίς να καταλάβει γιατί.
  const [receiptBlock, setReceiptBlock] = useState<string | null>(null);
  // «Περισσότερα»: το IRIS κάτω από τα γρήγορα Μετρητά/Κάρτα.
  const [moreMethods, setMoreMethods] = useState(false);

  // Τερματικά κάρτας. Αν η λίστα δεν φορτώσει, η χρέωση φεύγει χωρίς terminalId και ο
  // server διαλέγει μόνος του το μοναδικό ενεργό τερματικό του καταστήματος.
  const [employeeId] = useState(tokenEmployeeId);
  const [terminals, setTerminals] = useState<PosTerminal[]>([]);
  const [terminalId, setTerminalId] = useState<number | null>(null);
  const activeTerminal = terminals.find(t => t.id === terminalId) || null;

  useEffect(() => {
    let alive = true;
    Promise.resolve()
      .then(() => dineInApi.posTerminals(storeId))
      .then((data) => {
        if (!alive) return;
        const active: PosTerminal[] = (Array.isArray(data) ? data : []).filter(t => t && t.is_active !== false);
        if (active.length === 0) return;
        const saved = readSavedTerminal(employeeId, storeId);
        const pick = active.find(t => Number(t.id) === saved) || active.find(t => t.is_default) || active[0];
        setTerminals(active);
        setTerminalId(pick.id);
      })
      .catch((error) => console.error('Failed to load POS terminals:', error));
    return () => { alive = false; };
  }, [storeId, employeeId]);

  const chooseTerminal = (t: PosTerminal) => {
    setTerminalId(t.id);
    saveTerminal(employeeId, storeId, t.id);
  };

  // Κάρτα/IRIS σε εξέλιξη — το αίτημα περιμένει τον πελάτη στο τερματικό.
  const [cardWait, setCardWait] = useState<CardCharge | null>(null);
  const inFlight = useRef(false);
  // Χρέωση χωρίς απάντηση: ΚΑΘΕ είσπραξη κλειδώνει μέχρι να φανεί αν καταγράφηκε.
  // Ξεκινά από ό,τι έμεινε αβέβαιο για αυτή τη συνεδρία (η οθόνη έκλεισε και ξανάνοιξε).
  const [unconfirmed, setUnconfirmed] = useState<Unconfirmed | null>(() => {
    const kept = pendingUnconfirmed.get(Number(details?.session?.id));
    return kept ? { ...kept, checking: false } : null;
  });
  const lockedSession = useRef<number | null>(unconfirmed ? Number(unconfirmed.sessionId) : null);
  useEffect(() => {
    if (unconfirmed) {
      lockedSession.current = Number(unconfirmed.sessionId);
      pendingUnconfirmed.set(lockedSession.current, { ...unconfirmed, checking: false });
    } else if (lockedSession.current != null) {
      pendingUnconfirmed.delete(lockedSession.current);
      lockedSession.current = null;
    }
  }, [unconfirmed]);
  // Μετά από «δεν έχει καταγραφεί ακόμη» συνεχίζουμε να κοιτάμε: ο server γράφει την πληρωμή
  // ΑΦΟΥ απαντήσει το τερματικό και ο πάροχος, άρα μπορεί να φανεί λίγο αργότερα.
  const lateWatch = useRef<Unconfirmed | null>(null);
  const detailsAtCheck = useRef<any>(null);
  const [checkResult, setCheckResult] = useState<{ recorded: boolean; text: string } | null>(null);
  // Οριστική άρνηση ή πρόβλημα τερματικού: μένει ορατό, ο σερβιτόρος ξαναδοκιμάζει.
  const [cardIssue, setCardIssue] = useState<string | null>(null);

  const session = details?.session;

  // Μονάδες προς πληρωμή (ένα τεμάχιο ανά γραμμή), τι έχει ήδη εισπραχθεί και τι μένει —
  // η ΙΔΙΑ συνάρτηση με το ταμείο (utils/dineInMoney.tablePaymentState).
  const { unpaid, total, alreadyPaid, remainder } = useMemo(() => tablePaymentState(details), [details]);

  // Απόφαση 15: το PayableUnit (utils/dineInMoney) κρατά μόνο το όνομα μεγέθους· το προϊόν και
  // το size_id τα βρίσκουμε από τη γραμμή του server με το itemId, χωρίς να πειράξουμε το
  // κοινό module χρημάτων. Γραμμή που δεν βρέθηκε = το όνομα φαίνεται, όπως πριν.
  const linesById = useMemo(() => {
    const m = new Map<number, any>();
    for (const o of details?.orders || []) for (const it of o.items || []) m.set(Number(it.id), it);
    return m;
  }, [details]);
  const unitSize = (u: PayableUnit): string => {
    if (!u.size) return '';
    const line = linesById.get(Number(u.itemId));
    if (!line) return u.size;
    return showSizeName(products, line.product_id, { id: line.size_id, name: u.size }) ? u.size : '';
  };

  // Το υπόλοιπο που βλέπει ο σερβιτόρος = ό,τι χρεώνει η τελευταία δόση (σύνολο − εισπραγμένα),
  // όχι το άθροισμα των στρογγυλεμένων τιμών τεμαχίων («Ένα νούμερο παντού»).
  const due = remainder;
  const key = (u: PayableUnit) => `${u.itemId}:${u.instanceIndex}`;

  const selectedUnits = useMemo(
    () => unpaid.filter(u => selected.has(key(u))),
    [unpaid, selected]
  );

  // Στην πλήρη εξόφληση χρεώνουμε ΟΛΟ το υπόλοιπο· στο split μόνο τα επιλεγμένα.
  const units = mode === 'split' ? selectedUnits : unpaid;
  const amount = sliceAmount(units, unpaid, total, alreadyPaid);

  const givenNum = parseFloat(given.replace(',', '.')) || 0;
  const change = givenNum > 0 ? Math.max(0, parseFloat((givenNum - amount).toFixed(2))) : 0;

  const toggle = (u: PayableUnit) => {
    const k = key(u);
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k); else next.add(k);
      return next;
    });
  };

  // Ήρθαν φρέσκα στοιχεία ενώ η χρέωση είναι αβέβαιη. «Καταγράφηκε» μόλις το υπόλοιπο πέσει
  // όσο η χρέωση (ή κλείσει η συνεδρία) — και χωρίς «Έλεγχο», αν το φέρει το socket.
  // «Δεν καταγράφηκε» ΜΟΝΟ με στοιχεία που ήρθαν μετά το πάτημα του «Έλεγχος».
  useEffect(() => {
    const charge = unconfirmed || lateWatch.current;
    if (!charge) return;
    const sessionGone = !session || Number(session.id) !== Number(charge.sessionId);
    const drop = charge.dueBefore - due;
    const recorded =
      sessionGone ||
      (drop > MONEY_TOLERANCE && (due <= MONEY_TOLERANCE || drop >= charge.amount - RECORDED_SLACK));
    if (recorded) {
      const text = `Η πληρωμή ${money(charge.amount)} καταγράφηκε — μη χρεώσεις ξανά.`
        + (sessionGone ? ' Το τραπέζι εξοφλήθηκε.' : '');
      lateWatch.current = null;
      setUnconfirmed(null);
      setCheckResult({ recorded: true, text });
      setSelected(new Set());
      setGiven('');
      showToast('success', text);
    } else if (unconfirmed && unconfirmed.checking && details !== detailsAtCheck.current) {
      const text = `Δεν έχει καταγραφεί ακόμη πληρωμή ${money(unconfirmed.amount)}. `
        + 'Αν το τερματικό έδειξε ΕΓΚΡΙΣΗ, μη χρεώσεις ξανά: περίμενε λίγο ή πήγαινε στο ταμείο.';
      lateWatch.current = unconfirmed;
      setUnconfirmed(null);
      setCheckResult({ recorded: false, text });
      showToast('warning', text);
    }
  }, [details, session, due, unconfirmed, showToast]);

  // Το reload του γονέα απέτυχε (δεν ήρθαν στοιχεία): ξανά διαθέσιμο το «Έλεγχος», η είσπραξη μένει κλειδωμένη.
  const checking = unconfirmed?.checking === true;
  useEffect(() => {
    if (!checking) return;
    const t = setTimeout(() => setUnconfirmed(u => (u ? { ...u, checking: false } : u)), CHECK_RETRY_MS);
    return () => clearTimeout(t);
  }, [checking]);

  const checkPayment = () => {
    if (!unconfirmed || unconfirmed.checking) return;
    detailsAtCheck.current = details;
    setUnconfirmed({ ...unconfirmed, checking: true });
    onPaid(); // ο γονέας (TableSheet) ξαναφορτώνει το τραπέζι και μας δίνει νέα details
  };

  const pay = async (method: 'cash' | 'card' | 'iris') => {
    if (!session || units.length === 0 || inFlight.current) return;
    if (amount <= MONEY_TOLERANCE) {
      showToast('error', 'Δεν υπάρχει ποσό προς είσπραξη.');
      return;
    }
    const isCard = method !== 'cash';
    const terminal = isCard ? activeTerminal : null;
    const charge: CardCharge = { amount, iris: method === 'iris', terminalName: terminal ? terminalLabel(terminal) : null };
    const dueBefore = due;
    const sessionId = session.id;
    inFlight.current = true;
    // Παλιότερη αβέβαιη χρέωση που ακόμη παρακολουθούμε: αν η νέα προσπάθεια δεν χρεώσει τίποτα, μένει.
    const earlierWatch = lateWatch.current;
    lateWatch.current = null;
    setBusy(method);
    setCardIssue(null);
    setCheckResult(null);
    if (isCard) {
      setCardWait(charge);
      // Σημειώνεται από ΤΩΡΑ, εκτός οθόνης: αν η οθόνη χαθεί στη μέση της χρέωσης (αποσύνδεση,
      // επανεκκίνηση του app), το ξανάνοιγμα του τραπεζιού βρίσκει το κλείδωμα.
      pendingUnconfirmed.set(Number(sessionId), { ...charge, sessionId, dueBefore, checking: false });
    }
    // Η χρέωση έχει οριστικό αποτέλεσμα (πέρασε ή σίγουρα δεν έγινε): δεν μένει τίποτα αβέβαιο.
    const settled = () => { if (isCard) pendingUnconfirmed.delete(Number(sessionId)); };
    try {
      // ΡΗΤΟ quantity ανά itemId — χωρίς αυτό ο server χρεώνει ολόκληρη τη
      // γραμμή ως πληρωμένη (βλ. utils/dineInMoney.aggregatePaymentItems).
      const items = aggregatePaymentItems(units) as any;
      if (method === 'cash') {
        await dineInApi.cashPayment(storeId, session.id, { amount, items });
      } else {
        // Με όριο αναμονής: αν η απάντηση αργήσει πάνω από τον server, η χρέωση είναι ΑΒΕΒΑΙΗ·
        // αν έρθει αργότερα, ξαναφορτώνουμε το τραπέζι και το κλείδωμα λύνεται μόνο του.
        await withCardTimeout(
          dineInApi.cardPayment(storeId, session.id, {
            amount,
            items,
            isIris: method === 'iris',
            // Χωρίς λίστα τερματικών: ο server διαλέγει μόνος του το μοναδικό ενεργό.
            ...(terminal ? { terminalId: terminal.id } : {}),
          }),
          onPaid
        );
      }
      settled();
      const fullyPaid = units.length >= unpaid.length;
      showToast('success', fullyPaid ? 'Το τραπέζι εξοφλήθηκε.' : `Εισπράχθηκαν ${money(amount)}.`);
      onPaid();
      if (fullyPaid) onClose();
      else { setSelected(new Set()); setGiven(''); }
    } catch (error) {
      if (error instanceof DineInApiError && error.body?.error === 'receipt_required') {
        setReceiptBlock(error.message);
      }
      const guardCode = error instanceof DineInApiError && error.status === 409
        ? String(error.code ?? error.body?.code ?? error.body?.error ?? '')
        : '';
      const failure = isCard ? cardFailure(error) : null;
      if (GUARD_CODES.has(guardCode)) {
        // Ο φρουρός του server: μόνιμο μήνυμα και φρέσκα στοιχεία τραπεζιού (μετρητά και κάρτα).
        const message = (error as DineInApiError).message;
        settled();
        lateWatch.current = earlierWatch;
        setCardIssue(message);
        showToast('error', message);
        onPaid();
      } else if (failure?.kind === 'unconfirmed') {
        setUnconfirmed({ ...charge, sessionId, dueBefore, checking: false });
        showToast('error', 'Δεν ήρθε απάντηση από το τερματικό — κοίτα την οθόνη του πριν χρεώσεις ξανά.');
      } else if (failure) {
        settled();
        lateWatch.current = earlierWatch;
        setCardIssue(failure.message);
        showToast('error', failure.message);
      } else {
        // Κάθε άλλη απάντηση του server (400/409 πριν από το τερματικό) ή αποτυχία μετρητών: τίποτα δεν χρεώθηκε.
        settled();
        lateWatch.current = earlierWatch;
        showToast('error', error instanceof DineInApiError ? error.message : 'Η είσπραξη απέτυχε.');
      }
    } finally {
      inFlight.current = false;
      setBusy(null);
      setCardWait(null);
    }
  };

  // Το X της οθόνης: όσο περιμένουμε το τερματικό, το κλείσιμο θα έχανε την απάντηση
  // μιας χρέωσης που ίσως ήδη εγκρίθηκε.
  const requestClose = () => {
    if (cardWait) {
      showToast('warning', 'Περίμενε την απάντηση του τερματικού — αν κλείσεις τώρα, χάνεται το αποτέλεσμα της χρέωσης.');
      return;
    }
    onClose();
  };

  const payDisabled = !!busy || !!receiptBlock || !!unconfirmed || units.length === 0 || amount <= MONEY_TOLERANCE;
  // Μόνο ένδειξη: δεν κλειδώνει την είσπραξη (απόφαση ιδιοκτήτη 25/10/2026).
  const serverDoubts = useMemo(() => cardDoubtsOf(session), [session]);

  // Οι ειδοποιήσεις κάρτας μπαίνουν στο footer: εκεί κοιτάει ο σερβιτόρος (κουμπιά) και
  // δεν φεύγουν με το scroll της λίστας.
  const cardNotice = cardWait ? (
    <div
      role="status"
      data-testid="card-waiting"
      className="flex items-start gap-2 px-3 py-2 bg-blue-50 dark:bg-blue-900/30 border border-blue-300 dark:border-blue-700/50 rounded-xl text-blue-900 dark:text-blue-200 text-sm"
    >
      <Loader2 className="w-5 h-5 shrink-0 mt-0.5 animate-spin" />
      <div>
        <p className="font-bold">
          Το ποσό {money(cardWait.amount)} στάλθηκε στο τερματικό{cardWait.terminalName ? ` «${cardWait.terminalName}»` : ''}.
        </p>
        <p>
          Περίμενε να ολοκληρώσει ο πελάτης {cardWait.iris ? 'την πληρωμή IRIS' : 'με την κάρτα'}. Μην κλείσεις αυτή την οθόνη.
        </p>
      </div>
    </div>
  ) : unconfirmed ? (
    <div
      role="alert"
      data-testid="card-unconfirmed"
      className="space-y-2 px-3 py-2 bg-red-50 dark:bg-red-900/30 border-2 border-red-500 dark:border-red-600 rounded-xl text-red-900 dark:text-red-200 text-sm"
    >
      <div className="flex items-start gap-2">
        <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" />
        <div className="space-y-1">
          <p className="font-bold">
            Δεν ήρθε απάντηση για την πληρωμή {money(unconfirmed.amount)} με {unconfirmed.iris ? 'IRIS' : 'κάρτα'}.
            Η χρέωση μπορεί να έχει γίνει.
          </p>
          <p>Κοίτα το τερματικό{unconfirmed.terminalName ? ` «${unconfirmed.terminalName}»` : ''}:</p>
          <p>• Αν γράφει ΕΓΚΡΙΘΗΚΕ, <b>ΜΗ</b> χρεώσεις ξανά.</p>
          <p>• Αν περιμένει ακόμη την κάρτα, άφησε τον πελάτη να τελειώσει.</p>
          <p>Μετά πάτα «Έλεγχος» για να δεις αν καταγράφηκε.</p>
        </div>
      </div>
      <Button tone="danger" full disabled={unconfirmed.checking} onClick={checkPayment}>
        {unconfirmed.checking ? <Loader2 className="w-5 h-5 animate-spin" /> : <AlertTriangle className="w-5 h-5" />}
        Έλεγχος
      </Button>
    </div>
  ) : serverDoubts.length > 0 ? (
    // Από τον server: κάρτα που τρέχει από άλλη συσκευή, ή αβέβαιη χρέωση από πριν από ανανέωση του app.
    <CardDoubtBanner doubts={serverDoubts} />
  ) : checkResult ? (
    <div
      role={checkResult.recorded ? 'status' : 'alert'}
      data-testid="card-check-result"
      className={`flex items-start gap-2 px-3 py-2 rounded-xl text-sm border ${
        checkResult.recorded
          ? 'bg-emerald-50 dark:bg-emerald-900/30 border-emerald-300 dark:border-emerald-700/50 text-emerald-900 dark:text-emerald-200'
          : 'bg-amber-50 dark:bg-amber-900/30 border-amber-300 dark:border-amber-700/50 text-amber-900 dark:text-amber-200'
      }`}
    >
      {checkResult.recorded ? <Check className="w-5 h-5 shrink-0 mt-0.5" /> : <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" />}
      <span>{checkResult.text}</span>
    </div>
  ) : cardIssue ? (
    <div
      role="alert"
      data-testid="card-issue"
      className="flex items-start gap-2 px-3 py-2 bg-amber-50 dark:bg-amber-900/30 border border-amber-300 dark:border-amber-700/50 rounded-xl text-amber-900 dark:text-amber-200 text-sm"
    >
      <CreditCard className="w-5 h-5 shrink-0 mt-0.5" />
      <span>{cardIssue}</span>
    </div>
  ) : null;

  const cardLocked = !!cardWait || !!unconfirmed;

  // Πολλά τερματικά: επιλογή με τον αντίχειρα, θυμάται την επιλογή (ίδιο κλειδί με το ταμείο).
  // Ένα: μόνο το όνομα, να ξέρει ο σερβιτόρος σε ποιο θα εμφανιστεί το ποσό. Κανένα: τίποτα.
  const terminalPicker = terminals.length > 1 ? (
    <div role="group" aria-label="Τερματικό κάρτας" className="space-y-1">
      <p className="text-t3 text-[13px] px-1">Τερματικό για κάρτα / IRIS</p>
      <div className="flex flex-wrap gap-2">
        {terminals.map(t => {
          const on = t.id === activeTerminal?.id;
          return (
            <button
              key={t.id}
              type="button"
              aria-pressed={on}
              disabled={cardLocked}
              onClick={() => chooseTerminal(t)}
              className={`flex-1 min-w-[30%] min-h-[44px] px-3 rounded-xl font-bold text-sm truncate disabled:opacity-60
                ${on ? 'bg-cyan-600 text-white' : 'bg-surface-2 text-t2 active:bg-surface-3'}`}
            >
              {terminalLabel(t)}
            </button>
          );
        })}
      </div>
    </div>
  ) : activeTerminal ? (
    <p className="flex items-center justify-center gap-1.5 text-[13px] text-t3 px-1">
      <CreditCard className="w-4 h-4 shrink-0" />
      Κάρτα / IRIS στο τερματικό «{terminalLabel(activeTerminal)}»
    </p>
  ) : null;

  const footer = (
    <div className="space-y-2 pb-2">
      {cardNotice}
      <div className="flex items-baseline justify-between px-1">
        <span className="text-t3 text-sm">
          {mode === 'split' ? `Επιλεγμένα (${selectedUnits.length})` : 'Προς είσπραξη'}
        </span>
        <span className="text-2xl font-bold text-t1 tabular-nums">{money(amount)}</span>
      </div>
      {mode === 'change' && givenNum > 0 && (
        <div className="flex items-baseline justify-between px-1 pb-1">
          <span className="text-emerald-600 dark:text-emerald-400 text-sm font-semibold">Ρέστα</span>
          <span className="text-2xl font-bold text-emerald-600 dark:text-emerald-400 tabular-nums">{money(change)}</span>
        </div>
      )}
      {terminalPicker}
      <div className="grid grid-cols-2 gap-2">
        <Button tone="success" disabled={payDisabled} onClick={() => pay('cash')}>
          {busy === 'cash' ? <Loader2 className="w-5 h-5 animate-spin" /> : <Banknote className="w-5 h-5" />}
          Μετρητά
        </Button>
        <Button tone="primary" disabled={payDisabled} onClick={() => pay('card')}>
          {busy === 'card' ? <Loader2 className="w-5 h-5 animate-spin" /> : <CreditCard className="w-5 h-5" />}
          Κάρτα
        </Button>
      </div>
      {/* Περισσότερα: μόνο IRIS στο PDA («Επί πιστώσει» μόνο στο ταμείο). Ανοίγει/κλείνει πάντα —
          τα κουμπιά είσπραξης μέσα του κλειδώνουν όπως τα Μετρητά/Κάρτα. */}
      <button
        type="button"
        aria-expanded={moreMethods}
        aria-controls="pda-more-payment-methods"
        onClick={() => setMoreMethods(open => !open)}
        className="w-full min-h-[44px] px-4 rounded-xl border border-line-strong text-t3 text-sm font-semibold flex items-center justify-center gap-1.5 active:bg-surface-2"
      >
        {moreMethods ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
        Περισσότερα
      </button>
      {moreMethods && (
        <div id="pda-more-payment-methods">
          <Button tone="neutral" full disabled={payDisabled} onClick={() => pay('iris')}>
            {busy === 'iris' ? <Loader2 className="w-5 h-5 animate-spin" /> : <Smartphone className="w-5 h-5" />}
            IRIS
          </Button>
        </div>
      )}
    </div>
  );

  return (
    <FullScreen
      title={`Είσπραξη · Τραπέζι ${tableNumber}`}
      subtitle={alreadyPaid > 0 ? `Πληρωμένα ${money(alreadyPaid)} · Υπόλοιπο ${money(due)}` : `Σύνολο ${money(due)}`}
      onClose={requestClose}
      footer={footer}
    >
      {receiptBlock && (
        <div
          role="alert"
          className="mx-3 mt-2 flex items-start gap-2 px-3 py-2 bg-amber-50 dark:bg-amber-900/30 border border-amber-300 dark:border-amber-700/50 rounded-xl text-amber-900 dark:text-amber-200 text-sm"
        >
          <Receipt className="w-5 h-5 shrink-0 mt-0.5" />
          <span>{receiptBlock}</span>
        </div>
      )}

      {/* Επιλογή τρόπου */}
      <div className="grid grid-cols-3 gap-2 px-3 pt-2 pb-2">
        {([
          ['full', 'Όλα', <Check className="w-4 h-4" key="i" />],
          ['change', 'Ρέστα', <Banknote className="w-4 h-4" key="i" />],
          ['split', 'Χωριστά', <Split className="w-4 h-4" key="i" />],
        ] as Array<[Mode, string, React.ReactNode]>).map(([m, label, icon]) => (
          <button
            key={m}
            disabled={!!cardWait}
            onClick={() => { setMode(m); setSelected(new Set()); setGiven(''); }}
            className={`min-h-[48px] rounded-xl font-bold flex flex-col items-center justify-center gap-0.5 disabled:opacity-60
              ${mode === m ? 'bg-cyan-600 text-white' : 'bg-surface-1 text-t2 active:bg-surface-2'}`}
          >
            {icon}
            <span className="text-sm">{label}</span>
          </button>
        ))}
      </div>

      {/* Ρέστα: πληκτρολόγιο ποσού */}
      {mode === 'change' && (
        <div className="px-3 pb-3">
          <label className="block text-t3 text-sm mb-1.5">Πόσα έδωσε ο πελάτης;</label>
          <input
            type="number"
            inputMode="decimal"
            value={given}
            onChange={(e) => setGiven(e.target.value)}
            placeholder={amount.toFixed(2)}
            className="w-full px-3 py-2.5 bg-surface-2 dark:bg-surface-1 border border-line rounded-xl text-t1
                       text-2xl font-bold tabular-nums text-center focus:outline-none focus:border-cyan-500"
          />
          <div className="grid grid-cols-4 gap-2 mt-2">
            {[5, 10, 20, 50].map(v => (
              <button
                key={v}
                onClick={() => setGiven(String(v))}
                className="min-h-[44px] rounded-xl bg-surface-1 text-t2 font-bold text-base active:bg-surface-2"
              >
                {v}€
              </button>
            ))}
          </div>
          {givenNum > 0 && givenNum < amount && (
            <p className="text-amber-700 dark:text-amber-400 text-sm mt-2 px-1">
              Λείπουν {money(amount - givenNum)} — το ποσό που θα καταχωρηθεί είναι {money(amount)}.
            </p>
          )}
        </div>
      )}

      {/* Χωριστά: επιλογή τεμαχίων */}
      {mode === 'split' && (
        <div className="px-3 pb-3 space-y-2">
          <p className="text-t3 text-sm px-1">Πάτα ό,τι πληρώνει αυτός ο πελάτης.</p>
          {unpaid.length === 0 && (
            <p className="text-t4 text-center py-6">Όλα τα είδη έχουν πληρωθεί.</p>
          )}
          {unpaid.map(u => {
            const on = selected.has(key(u));
            const sz = unitSize(u);
            return (
              <button
                key={key(u)}
                disabled={!!cardWait}
                onClick={() => toggle(u)}
                className={`w-full min-h-[48px] flex items-center gap-2.5 px-3 py-2 rounded-xl text-left transition-colors
                  ${on ? 'bg-cyan-600/20 border border-cyan-500' : 'bg-surface-2 dark:bg-surface-1 border border-line active:bg-surface-2'}`}
              >
                <div className={`w-6 h-6 rounded-md shrink-0 flex items-center justify-center
                  ${on ? 'bg-cyan-500' : 'bg-surface-2 light:border-2 border-line-strong dark:border-transparent'}`}>
                  {on && <Check className="w-4 h-4 light:text-white dark:text-t1" />}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-t1 text-[15px] font-semibold truncate">{u.name}</p>
                  {sz && <p className="text-t3 text-[13px] truncate">{sz}</p>}
                </div>
                <span className="text-t1 text-[15px] font-bold tabular-nums">{money(u.price)}</span>
              </button>
            );
          })}
        </div>
      )}

      {/* Όλα: απλή σύνοψη */}
      {mode === 'full' && (
        <div className="px-3 pb-3 space-y-2">
          {unpaid.length === 0 ? (
            <p className="text-t4 text-center py-6">Το τραπέζι είναι εξοφλημένο.</p>
          ) : (
            unpaid.map(u => {
              const sz = unitSize(u);
              return (
              <div key={key(u)} className="flex items-center gap-2.5 px-3 py-2 rounded-xl bg-surface-1">
                <div className="min-w-0 flex-1">
                  <p className="text-t1 text-[15px] font-semibold truncate">{u.name}</p>
                  {sz && <p className="text-t3 text-[13px] truncate">{sz}</p>}
                </div>
                <span className="text-t1 text-[15px] font-bold tabular-nums">{money(u.price)}</span>
              </div>
              );
            })
          )}
        </div>
      )}
    </FullScreen>
  );
};

export default PaymentScreen;
